/**
 * A map's tier, as the rarity it implies — shared by validation and the engine.
 *
 * `MapItemData.setTier` re-derives the map's gear rarity from its tier, and that rarity decides
 * how many affixes it carries and how hard they roll. See `damage/map.ts` in the engine for the
 * rest of what a map does.
 */
import { CATEGORY, entry, ids } from "./queries.js";
function minMax(value) {
    if (value === null || typeof value !== "object")
        return undefined;
    const v = value;
    const min = v["min"];
    const max = v["max"];
    return typeof min === "number" && typeof max === "number" ? { min, max } : undefined;
}
/**
 * The `NORMAL` rarities, the only ones a map can be. Unique and Runeword keep a default 0-100
 * `map_tiers` and would otherwise match every tier — the fork's own comment on `rarityForTier`.
 */
function normalRarities(snapshot) {
    const out = [];
    for (const id of ids(snapshot, CATEGORY.gearRarity)) {
        const d = entry(snapshot, CATEGORY.gearRarity, id)?.data;
        if (!d || d["type"] !== "NORMAL")
            continue;
        const tiers = minMax(d["map_tiers"]);
        const statPercents = minMax(d["stat_percents"]);
        if (!tiers || !statPercents)
            continue;
        out.push({
            id,
            tiers,
            statPercents,
            affixCount: typeof d["min_affixes"] === "number" ? d["min_affixes"] : 0,
            itemTier: typeof d["item_tier"] === "number" ? d["item_tier"] : 0,
        });
    }
    return out;
}
/**
 * `MapItemData.maxMapTier()` — the top `NORMAL` rarity's `map_tiers.max`. 100 on this pack, and
 * derived rather than written down because the game derives it too.
 */
export function maxMapTier(snapshot) {
    const tops = normalRarities(snapshot).map((r) => r.tiers.max);
    return tops.length === 0 ? 100 : Math.max(...tops);
}
/**
 * `MapItemData.rarityForTier` — the `NORMAL` rarity whose band holds this tier.
 *
 *     if (r.map_tiers.min > tier) continue;
 *     if (best == null || r.map_tiers.min > best.map_tiers.min
 *             || (r.map_tiers.min == best.map_tiers.min && r.item_tier > best.item_tier)) best = r;
 */
export function mapRarityForTier(snapshot, tier) {
    let best;
    for (const r of normalRarities(snapshot)) {
        if (r.tiers.min > tier)
            continue;
        if (best === undefined ||
            r.tiers.min > best.tiers.min ||
            (r.tiers.min === best.tiers.min && r.itemTier > best.itemTier)) {
            best = r;
        }
    }
    if (best === undefined)
        return undefined;
    const { itemTier: _itemTier, ...rarity } = best;
    return rarity;
}
//# sourceMappingURL=maps.js.map